import { ethers } from 'ethers';
import api from './api';
import { BASE_CHAIN_PARAMS } from './chainParams';

const DEFAULT_BUFFER_ETH = 0.0001;
const POLL_MS = 800;
const POLL_ATTEMPTS = 10;

const RELAYER_OUT_OF_GAS_MESSAGE =
  'Platform gas drip is unavailable right now. Add a little Base ETH to your wallet (usually a few cents) so you can confirm the transaction yourself.';

const JACKPOT_MIN_MESSAGE =
  'Gas drip requires a minimum claimable Free jackpot balance. Accumulate more Free jackpot wins, or fund a little Base ETH yourself to claim smaller amounts.';

const FUND_ETH_FOOTER =
  'Bridge or buy a small amount of ETH on <strong>Base</strong>, then try again. Most actions need only a few cents of gas.';

let relayerGasModalEl = null;
let relayerCountdownTimer = null;

function getAuthUserId() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.userId ? String(payload.userId) : null;
  } catch {
    return null;
  }
}

function isRelayerOutOfGasError(e) {
  const code = e?.response?.data?.code;
  const msg = String(e?.response?.data?.message || e?.message || '').toLowerCase();
  return (
    code === 'RELAYER_OUT_OF_GAS' ||
    code === 'DRIP_BUDGET_EXHAUSTED' ||
    msg.includes('insufficient balance to drip') ||
    msg.includes('no fund to drip gas') ||
    msg.includes('relayer has insufficient') ||
    msg.includes('budget is exhausted')
  );
}

function isDripCooldownError(e) {
  const code = e?.response?.data?.code;
  return (
    code === 'FREE_DRIP_COOLDOWN' ||
    code === 'DRIP_COOLDOWN' ||
    code === 'DRIP_DAILY_CAP' ||
    code === 'DRIP_BUSY' ||
    code === 'DRIP_DISABLED' ||
    code === 'PRIMARY_WALLET_ONLY' ||
    code === 'JACKPOT_MIN_NOT_MET' ||
    code === 'ACCOUNT_TOO_NEW' ||
    code === 'USER_MISMATCH' ||
    code === 'RELAYER_PATH_DEPRECATED' ||
    code === 'DRIP_VOUCHER_REQUIRED' ||
    code === 'DRIP_VOUCHER_INVALID' ||
    code === 'DRIP_VOUCHER_USED' ||
    code === 'DRIP_VOUCHER_EXPIRED' ||
    code === 'DRIP_VOUCHER_WALLET' ||
    code === 'DRIP_PURPOSE_BLOCKED' ||
    code === 'DRIP_PURPOSE_INVALID' ||
    code === 'DRIP_NO_ACTIVITY' ||
    code === 'UNUSED_DRIP_PENALTY' ||
    code === 'DRIP_IP_CAP' ||
    code === 'DRIP_BUDGET_EXHAUSTED' ||
    code === 'WALLET_NOT_LINKED' ||
    e?.response?.status === 429
  );
}

function closeRelayerGasModal() {
  if (relayerCountdownTimer) {
    window.clearInterval(relayerCountdownTimer);
    relayerCountdownTimer = null;
  }
  if (relayerGasModalEl?.parentNode) {
    relayerGasModalEl.parentNode.removeChild(relayerGasModalEl);
  }
  relayerGasModalEl = null;
  document.body.style.overflow = '';
}

function formatRemainingMs(ms) {
  if (!(ms > 0)) return '';
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function remainingFromIso(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, t - Date.now());
}

function formatNextEligible(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
}

/**
 * Dismissible modal — explains why drip failed and what to do (fund Base ETH).
 */
export function showRelayerOutOfGasModal(message = RELAYER_OUT_OF_GAS_MESSAGE, opts = {}) {
  closeRelayerGasModal();

  const title = opts.title || 'Gas funding unavailable';
  const playType = normalizePlayType(opts.playType);
  const playTypeLabel = playType === 'free' ? 'Free jackpot' : playType === 'boost' ? 'Boost' : 'Market';
  const nextMs = remainingFromIso(opts.nextEligibleAt);
  const nextLine = nextMs > 0
    ? `<p class="text-sm font-semibold text-amber-700 dark:text-amber-300">Next relayer privilege: <span data-relayer-countdown>${formatRemainingMs(nextMs)}</span></p>`
    : opts.nextEligibleAt
      ? `<p class="text-sm font-semibold text-amber-700 dark:text-amber-300">Next eligibility: ${formatNextEligible(opts.nextEligibleAt)}</p>`
      : '';
  const minLine =
    opts.minJackpotUsd != null
      ? `<p class="text-sm font-semibold text-amber-700 dark:text-amber-300">Minimum Free jackpot for gas drip: $${Number(opts.minJackpotUsd).toFixed(2)}${
          opts.claimableJackpotUsd != null
            ? ` (you have $${Number(opts.claimableJackpotUsd).toFixed(2)})`
            : ''
        }</p>`
      : '';

  const primaryTrunc = opts.primaryWalletTruncated
    ? String(opts.primaryWalletTruncated).replace(/</g, '&lt;')
    : '';
  const primaryEligible = opts.primaryEligible === true;
  const primaryHint =
    opts.code === 'PRIMARY_WALLET_ONLY' && primaryTrunc
      ? primaryEligible
        ? `<div class="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-xs text-emerald-900 dark:text-emerald-100 space-y-1">
            <p class="font-semibold">Relayer wallet</p>
            <p>Switch MetaMask to your first linked wallet <strong class="font-mono">${primaryTrunc}</strong> to use platform gas drip (still has allocation), or fund this wallet with Base ETH.</p>
          </div>`
        : `<div class="rounded-lg bg-stone-50 dark:bg-stone-900/50 border border-stone-200 dark:border-stone-700 px-3 py-2 text-xs text-stone-800 dark:text-stone-200 space-y-1">
            <p class="font-semibold">Relayer wallet</p>
            <p>Only <strong class="font-mono">${primaryTrunc}</strong> (first linked) can use gas drip, and its allocation is currently used up. Fund <strong>this</strong> wallet with Base ETH to continue now.</p>
          </div>`
      : '';

  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'relayer-gas-modal-title');
  overlay.className =
    'fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 sm:p-6';
  overlay.style.paddingTop = 'max(1rem, env(safe-area-inset-top))';
  overlay.style.paddingBottom = 'max(1rem, env(safe-area-inset-bottom))';

  overlay.innerHTML = `
    <div class="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl overflow-hidden" data-relayer-gas-panel>
      <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-4 sm:px-6">
        <h2 id="relayer-gas-modal-title" class="text-lg font-bold text-gray-900 dark:text-white">
          ${String(title).replace(/</g, '&lt;')}
        </h2>
        <button type="button" data-relayer-gas-close aria-label="Close"
          class="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div class="px-4 py-4 sm:px-6 sm:py-5 space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        <p>${String(message).replace(/</g, '&lt;')}</p>
        ${minLine}
        ${nextLine}
        ${primaryHint}
        <div class="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-100 space-y-1">
          <p class="font-semibold">What to do</p>
          <p>${FUND_ETH_FOOTER}</p>
          <p>Platform gas drip only helps for eligible Jackpot / Boost / Market actions — not for plain USDC transfers. After a drip, confirm the action in your wallet so your eligibility stays healthy.</p>
          ${
            playType === 'free'
              ? `<p>Or keep winning Free predictions until your claimable jackpot reaches the minimum for the ${playTypeLabel} drip.</p>`
              : ''
          }
        </div>
      </div>
      <div class="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6">
        <button type="button" data-relayer-gas-close
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
          OK
        </button>
      </div>
    </div>
  `;

  const onClose = (e) => {
    if (e.target === overlay || e.target.closest('[data-relayer-gas-close]')) {
      closeRelayerGasModal();
    }
  };
  overlay.addEventListener('click', onClose);

  document.body.style.overflow = 'hidden';
  document.body.appendChild(overlay);
  relayerGasModalEl = overlay;

  if (opts.nextEligibleAt && remainingFromIso(opts.nextEligibleAt) > 0) {
    if (relayerCountdownTimer) window.clearInterval(relayerCountdownTimer);
    relayerCountdownTimer = window.setInterval(() => {
      const el = overlay.querySelector('[data-relayer-countdown]');
      if (!el || !overlay.isConnected) {
        window.clearInterval(relayerCountdownTimer);
        relayerCountdownTimer = null;
        return;
      }
      const left = remainingFromIso(opts.nextEligibleAt);
      el.textContent = left > 0 ? formatRemainingMs(left) : 'now';
      if (!(left > 0)) {
        window.clearInterval(relayerCountdownTimer);
        relayerCountdownTimer = null;
      }
    }, 1000);
  }
}

async function readEthBalance(address) {
  if (!address) return Number.NaN;
  if (typeof window !== 'undefined' && window.ethereum) {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const network = await provider.getNetwork();
      if (Number(network.chainId) === Number(BASE_CHAIN_PARAMS.chainIdDecimal)) {
        const bal = await provider.getBalance(address);
        return parseFloat(ethers.formatEther(bal));
      }
    } catch {
      /* fall through */
    }
  }
  const urls = BASE_CHAIN_PARAMS.rpcUrls || [];
  for (let i = 0; i < urls.length; i += 1) {
    try {
      const provider = new ethers.JsonRpcProvider(urls[i], BASE_CHAIN_PARAMS.chainIdDecimal, {
        staticNetwork: true,
      });
      // eslint-disable-next-line no-await-in-loop
      const bal = await provider.getBalance(address);
      return parseFloat(ethers.formatEther(bal));
    } catch {
      /* try next */
    }
  }
  return Number.NaN;
}

export const GAS_DRIP_BLOCKED = 'GAS_DRIP_BLOCKED';

export function isGasDripBlocked(error) {
  const code = error?.code;
  const msg = String(error?.message || '');
  return code === GAS_DRIP_BLOCKED || msg === GAS_DRIP_BLOCKED;
}

export function throwGasDripBlocked() {
  const err = new Error(GAS_DRIP_BLOCKED);
  err.code = GAS_DRIP_BLOCKED;
  throw err;
}

export async function hasSufficientEthForGas(address, requiredEth = 0, bufferEth = DEFAULT_BUFFER_ETH) {
  if (!address) return false;
  const balance = await readEthBalance(address);
  const needed = parseFloat(requiredEth || 0) + bufferEth;
  if (!Number.isFinite(balance) || !Number.isFinite(needed)) return false;
  return balance >= needed;
}

export async function assertGasOrDrip(walletAddress, opts) {
  const ok = await ensureGasOrDrip(walletAddress, opts);
  if (!ok) throwGasDripBlocked();
  return true;
}

function normalizePlayType(labelOrType) {
  const t = String(labelOrType || 'market').toLowerCase();
  if (t === 'free' || t === 'boost' || t === 'market') return t;
  if (t.includes('boost')) return 'boost';
  if (t.includes('free') || t.includes('jackpot')) return 'free';
  return 'market';
}

function purposeFromOpts({ purpose, label, playType }) {
  if (purpose) return String(purpose).trim();
  const s = String(label || '').toLowerCase();
  if (s.includes('transfer') || s.includes('send usdc')) return 'usdc_transfer';
  if (s.includes('jackpot')) return 'jackpot_claim';
  if (s.includes('nft') && s.includes('reward')) return 'nft_reward_claim';
  if (s.includes('vault') && s.includes('deposit')) return 'vault_deposit';
  if (s.includes('vault') && s.includes('withdraw')) return 'vault_withdraw';
  if (s.includes('add')) return 'boost_add';
  if (s.includes('withdraw') && playType === 'boost') return 'boost_withdraw';
  if (s.includes('claim') && playType === 'boost') return 'boost_claim';
  if (s.includes('boost') || s.includes('stake')) return 'boost_stake';
  if (s.includes('sell')) return 'market_sell';
  if (s.includes('buy') || s.includes('trade')) return 'market_buy';
  if (s.includes('claim') && playType === 'market') return 'market_claim';
  if (s.includes('approval') || s.includes('approve')) return 'usdc_approval';
  if (playType === 'free') return 'jackpot_claim';
  if (playType === 'boost') return 'boost_stake';
  return 'market_buy';
}

function denyGasWithModal(showNotification, message, modalOpts, notifyType = 'warning') {
  showNotification?.(message, notifyType, notifyType === 'warning' ? 10000 : 8000);
  showRelayerOutOfGasModal(message, modalOpts);
  return false;
}

function modalTitleForCode(code, typeLabel) {
  switch (code) {
    case 'JACKPOT_MIN_NOT_MET':
      return 'Minimum Free jackpot required';
    case 'ACCOUNT_TOO_NEW':
      return 'Account too new';
    case 'DRIP_DAILY_CAP':
    case 'DRIP_IP_CAP':
      return `${typeLabel} gas drip limit reached`;
    case 'DRIP_DISABLED':
    case 'DRIP_BUDGET_EXHAUSTED':
      return 'Gas drip unavailable';
    case 'PRIMARY_WALLET_ONLY':
      return 'Use your first linked wallet';
    case 'DRIP_NO_ACTIVITY':
      return 'Play first, or fund Base ETH';
    case 'UNUSED_DRIP_PENALTY':
      return 'Not eligible for gas drip';
    case 'DRIP_COOLDOWN':
    case 'FREE_DRIP_COOLDOWN':
      return `${typeLabel} gas drip on cooldown`;
    case 'DRIP_PURPOSE_BLOCKED':
      return 'Fund Base ETH for transfers';
    case 'DRIP_VOUCHER_REQUIRED':
    case 'DRIP_VOUCHER_INVALID':
    case 'DRIP_VOUCHER_USED':
    case 'DRIP_VOUCHER_EXPIRED':
      return 'Restart this action';
    case 'DRIP_BUSY':
      return `${typeLabel} gas drip in progress`;
    default:
      return `${typeLabel} gas drip unavailable`;
  }
}

/**
 * Notify backend that a real platform tx completed after a drip (stops unused-drip penalty).
 */
export async function notifyGasDripActionCompleted({
  walletAddress,
  txHash,
  playType,
  purpose,
  label,
} = {}) {
  try {
    const uid = getAuthUserId();
    if (!uid || !txHash) return;
    await api.post(`/relayer/users/${uid}/drip-complete`, {
      walletAddress,
      actionTxHash: txHash,
      playType: normalizePlayType(playType || label),
      purpose: purposeFromOpts({ purpose, label, playType: normalizePlayType(playType || label) }),
    });
  } catch {
    /* non-blocking */
  }
}

/**
 * Check wallet Base ETH first. Only request a drip when short.
 * Issues an intent voucher, then redeems it via gasdrip.
 */
export async function ensureGasOrDrip(
  walletAddress,
  {
    label,
    playType,
    purpose,
    showNotification,
    requiredEth = 0,
    userId,
    claimAmountUsdc,
    skipDrip = false,
    allowDrip = true,
  } = {}
) {
  if (!walletAddress) return true;
  const type = normalizePlayType(playType || label);
  const resolvedPurpose = purposeFromOpts({ purpose, label, playType: type });

  // Plain USDC transfers never get platform drip — tell the user clearly.
  if (skipDrip || allowDrip === false || resolvedPurpose === 'usdc_transfer') {
    const needed = parseFloat(requiredEth || 0) + DEFAULT_BUFFER_ETH;
    let bal = Number.NaN;
    try {
      bal = await readEthBalance(walletAddress);
    } catch {
      bal = Number.NaN;
    }
    if (Number.isFinite(bal) && bal >= needed) return true;
    return denyGasWithModal(
      showNotification,
      'This action needs a little Base ETH in your wallet for gas. Platform gas drip is not available for USDC transfers — add ETH on Base, then try again.',
      { title: 'Base ETH required', playType: type }
    );
  }

  const uid = userId || getAuthUserId();
  if (!uid) {
    return denyGasWithModal(
      showNotification,
      'You must be logged in to use gas drip. Fund a little Base ETH to continue without the relayer.',
      { title: 'Login required for gas drip', playType: type }
    );
  }

  const needed = parseFloat(requiredEth || 0) + DEFAULT_BUFFER_ETH;
  let currentBalance = Number.NaN;
  try {
    currentBalance = await readEthBalance(walletAddress);
  } catch {
    currentBalance = Number.NaN;
  }

  if (Number.isFinite(currentBalance) && currentBalance >= needed) return true;

  try {
    let voucher;
    try {
      const { data } = await api.post(`/relayer/users/${uid}/drip-voucher`, {
        walletAddress,
        playType: type,
        purpose: resolvedPurpose,
        label: label || resolvedPurpose,
        claimAmountUsdc: claimAmountUsdc != null ? Number(claimAmountUsdc) : undefined,
      });
      voucher = data;
    } catch (e) {
      if (isDripCooldownError(e) || e?.response?.status === 403) {
        const code = e?.response?.data?.code;
        const msg =
          e?.response?.data?.message ||
          'Gas drip is not available for this action. Fund Base ETH to continue.';
        const typeLabel = type === 'free' ? 'Free jackpot' : type === 'boost' ? 'Boost' : 'Market';
        return denyGasWithModal(showNotification, msg, {
          title: modalTitleForCode(code, typeLabel),
          playType: type,
          code,
          nextEligibleAt: e?.response?.data?.nextEligibleAt,
          minJackpotUsd: e?.response?.data?.minJackpotUsd,
          claimableJackpotUsd: e?.response?.data?.claimableJackpotUsd,
          primaryWalletTruncated: e?.response?.data?.primaryWalletTruncated,
          primaryEligible: e?.response?.data?.primaryEligible,
        });
      }
      throw e;
    }

    try {
      const { data } = await api.post(`/relayer/users/${uid}/gasdrip`, {
        userId: uid,
        walletAddress,
        playType: voucher?.playType || type,
        label: label || type,
        purpose: voucher?.purpose || resolvedPurpose,
        claimAmountUsdc: claimAmountUsdc != null ? Number(claimAmountUsdc) : undefined,
        voucherId: voucher?.voucherId,
        voucherToken: voucher?.voucherToken,
      });
      if (data?.sent) {
        showNotification?.(
          'Gas funded for you — confirm the action in your wallet now. Canceling unused drips can pause future gas help.',
          'success',
          9000
        );
      } else if (data?.ok && data?.sent === false) {
        return true;
      } else {
        const after = await readEthBalance(walletAddress);
        if (Number.isFinite(after) && after >= needed) return true;
        return denyGasWithModal(
          showNotification,
          'Need a little Base ETH to pay gas. Fund your wallet on Base, then try again.',
          { title: 'Base ETH required', playType: type }
        );
      }
    } catch (e) {
      if (isDripCooldownError(e)) {
        const code = e?.response?.data?.code;
        const msg =
          e?.response?.data?.message ||
          'Gas drip is not available right now. Fund Base ETH to continue.';
        const responseType = normalizePlayType(e?.response?.data?.playType || type);
        const typeLabel =
          responseType === 'free' ? 'Free jackpot' : responseType === 'boost' ? 'Boost' : 'Market';
        return denyGasWithModal(showNotification, msg, {
          title: modalTitleForCode(code, typeLabel),
          playType: responseType,
          code,
          nextEligibleAt: e?.response?.data?.nextEligibleAt,
          minJackpotUsd: e?.response?.data?.minJackpotUsd,
          claimableJackpotUsd: e?.response?.data?.claimableJackpotUsd,
          primaryWalletTruncated: e?.response?.data?.primaryWalletTruncated,
          primaryEligible: e?.response?.data?.primaryEligible,
        });
      }
      if (isRelayerOutOfGasError(e)) {
        const msg = e?.response?.data?.message || RELAYER_OUT_OF_GAS_MESSAGE;
        return denyGasWithModal(showNotification, msg, { playType: type }, 'error');
      }
      const typeLabel = type === 'free' ? 'Free jackpot' : type === 'boost' ? 'Boost' : 'Market';
      const msg =
        e?.response?.data?.message ||
        `Unable to fund gas${label ? ` for ${label}` : ''}. Please add a little Base ETH or try again.`;
      return denyGasWithModal(
        showNotification,
        msg,
        { title: `${typeLabel} gas funding unavailable`, playType: type },
        'error'
      );
    }

    for (let i = 0; i < POLL_ATTEMPTS; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_MS));
      // eslint-disable-next-line no-await-in-loop
      const ok = await hasSufficientEthForGas(walletAddress, requiredEth);
      if (ok) return true;
    }

    showNotification?.(
      'Gas was funded but has not arrived yet. Wait a moment, or fund Base ETH and retry.',
      'warning'
    );
    return false;
  } catch {
    return denyGasWithModal(
      showNotification,
      'Need a little Base ETH to pay gas. Fund your wallet on Base, then try again.',
      { title: 'Base ETH required', playType: type }
    );
  }
}
